import { Directive, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { FilterKey } from './filter.keys.type';
import { FilterService } from './filter.service';
import { ParserService } from './services/parser.service';

@Directive()
export abstract class BaseFilterDirective<T> implements OnInit {
  @Output() modelChange = new EventEmitter<T>();
  @Input() nullIfEmpty = false;
  public selected$!: Observable<T>;

  protected abstract readonly filterService: FilterService;
  protected abstract readonly parserService: ParserService<T>;

  /* eslint-disable  @angular-eslint/prefer-inject */
  protected constructor(
    protected readonly queryParamField: FilterKey | Observable<FilterKey>,
  ) {}
  /* eslint-enable  @angular-eslint/prefer-inject */

  private queryParam$!: Observable<FilterKey>;

  ngOnInit(): void {
    this.queryParam$ =
      typeof this.queryParamField === 'string'
        ? of(this.queryParamField)
        : this.queryParamField;
    this.selected$ = this.initializeSelected();
  }

  protected initializeSelected(): Observable<T> {
    return this.queryParam$.pipe(
      switchMap((queryParam) =>
        this.filterService.getValueFromQueryParam(queryParam).pipe(
          map((params) =>
            this.parserService.unparse(params ?? '', this.nullIfEmpty),
          ),
          map((selected) => this.trimValue(selected)),
          tap((selected) => this.modelChange.emit(selected)),
        ),
      ),
    );
  }

  public saveSelected(selected: T): Promise<boolean> {
    const trimmed = this.trimValue(selected);
    this.modelChange.emit(trimmed);
    return firstValueFrom(
      this.queryParam$.pipe(
        switchMap((queryParam) =>
          this.filterService.saveValueInQueryParam(
            queryParam,
            this.parserService.parse(trimmed),
          ),
        ),
      ),
    );
  }

  private trimValue(value: T): T {
    return typeof value === 'string' ? (value.trim() as T) : value;
  }
}
